import { Action, createReducer, on } from '@ngrx/store';
import * as actions from './course-search.actions';
import { CourseSearch } from '../../models/course-search.models';
import {
	SingleClearingCourseSearchUni,
	SingleCourseSearchUni,
} from '../../models/single-uni-course-search.models';
import { ClearingCourseSearch } from '../../models/clearing-course-search.models';
import { CourseSearchFilterPill } from '../../models/course-search-filter.models';
import {
	CourseSearchPayload,
	UniversityCourseSearchPayload,
} from '../../models/course-search-payload.models';
import {
	COURSE_SEARCH_DEFAULT_PAYLOAD,
	UNIVERSITY_COURSE_SEARCH_DEFAULT_PAYLOAD,
} from '../../static/course-search-payload.properties';
import { DegreeLevelPayload } from '@uc/web/shared/data-models';

export const _STATE_COURSE_SEARCH_FEATURE_KEY = 'stateCourseSearchV2';

export interface CourseSearchState {
	courses: CourseSearch | null;
	clearingCourses: ClearingCourseSearch | null;

	uni: SingleCourseSearchUni | null;
	clearingUni: SingleClearingCourseSearchUni | null;

	courseSearchPayload: CourseSearchPayload;
	uniCourseSearchPayload: UniversityCourseSearchPayload;

	clearingCourseSearchPayload: CourseSearchPayload;
	clearingUniCourseSearchPayload: UniversityCourseSearchPayload;

	filterPills: CourseSearchFilterPill[];
	filterToBeRemoved: CourseSearchFilterPill | null;

	isLoading: boolean;
	isLoadingPagination: boolean;

	error: any;
}

export interface StateCourseSearchPartialState {
	readonly [_STATE_COURSE_SEARCH_FEATURE_KEY]: CourseSearchState;
}

export const initialState: CourseSearchState = {
	courses: null,
	clearingCourses: null,

	uni: null,
	clearingUni: null,

	courseSearchPayload: { ...COURSE_SEARCH_DEFAULT_PAYLOAD },
	uniCourseSearchPayload: { ...UNIVERSITY_COURSE_SEARCH_DEFAULT_PAYLOAD },

	clearingCourseSearchPayload: {
		...COURSE_SEARCH_DEFAULT_PAYLOAD,
		degreeLevel: 'ALL_CLEARING',
	},
	clearingUniCourseSearchPayload: {
		...UNIVERSITY_COURSE_SEARCH_DEFAULT_PAYLOAD,
		degreeLevel: 'ALL_CLEARING',
	},
	filterPills: [],
	filterToBeRemoved: null,

	isLoading: false, //shows the loading cards (no courses remain)
	isLoadingPagination: false, // shows animation within load more button (courses remain in view)

	error: null,
};

const courseSearchReducer = createReducer(
	initialState,

	on(
		actions.fetchCourses,
		actions.fetchUniCourses,
		actions.fetchClearingCourses,
		actions.fetchClearingUniCourses,
		(state) => ({
			...state,
			isLoading: true,
			isLoadingPagination: false,
			error: null,
		}),
	),

	on(
		actions.fetchPaginatedCourses,
		actions.fetchPaginatedUniCourses,
		actions.fetchPaginatedClearingCourses,
		actions.fetchPaginatedClearingUniCourses,
		(state) => ({
			...state,
			isLoading: false,
			isLoadingPagination: true,
			error: null,
		}),
	),

	// courses
	on(actions.updateCourses, (state, { courses }) => {
		return {
			...state,
			uni: null,
			clearingUni: null,
			clearingCourses: null,
			courses: {
				...courses,
			},
			shouldLoadCourses: false,
			isLoading: false,
			isLoadingPagination: false,
			error: null,
		};
	}),

	on(actions.updatePaginatedCourses, (state, { courses }) => {
		return {
			...state,
			uni: null,
			clearingUni: null,
			clearingCourses: null,
			courses: {
				...courses,
				data: [...(state.courses?.data || []), ...courses.data],
			},
			shouldLoadCourses: false,
			isLoading: false,
			isLoadingPagination: false,
			error: null,
		};
	}),

	on(actions.updateUniCourses, (state, { uni }) => {
		return {
			...state,
			courses: null,
			clearingUni: null,
			clearingCourses: null,
			uni: {
				...state.uni,
				...uni,
			},
			shouldLoadCourses: false,
			isLoading: false,
			isLoadingPagination: false,
			error: null,
		};
	}),

	on(actions.updatePaginatedUniCourses, (state, { uni }) => {
		const courses = [
			...(state.uni?.courses?.data ?? []),
			...(uni.courses?.data ?? []),
		];
		return {
			...state,
			courses: null,
			clearingUni: null,
			clearingCourses: null,
			uni: {
				...state.uni,
				...uni,
				courses: {
					...state.uni?.courses,
					...uni.courses,
					data: courses,
				},
			},
			shouldLoadCourses: false,
			isLoading: false,
			isLoadingPagination: false,
			error: null,
		};
	}),

	// clearing
	on(actions.updateClearingCourses, (state, { clearingCourses }) => {
		return {
			...state,
			uni: null,
			clearingUni: null,
			courses: null,
			clearingCourses: {
				...state.clearingCourses,
				...clearingCourses,
			},
			shouldLoadCourses: false,
			isLoading: false,
			isLoadingPagination: false,
			error: null,
		};
	}),

	on(actions.updatePaginatedClearingCourses, (state, { clearingCourses }) => {
		return {
			...state,
			uni: null,
			courses: null,
			clearingUni: null,
			clearingCourses: {
				...clearingCourses,
				data: [...(state.clearingCourses?.data || []), ...clearingCourses.data],
			},
			shouldLoadCourses: false,
			isLoading: false,
			isLoadingPagination: false,
			error: null,
		};
	}),

	on(actions.updatePaginatedClearingUniCourses, (state, { clearingUni }) => {
		const clearingCourses = [
			...(state.clearingUni?.courses?.data ?? []),
			...(clearingUni.courses?.data ?? []),
		];
		return {
			...state,
			courses: null,
			uni: null,
			clearingCourses: null,
			clearingUni: {
				...state.clearingUni,
				...clearingUni,
				courses: {
					...state.clearingUni?.courses,
					...clearingUni.courses,
					per_page: clearingUni.courses?.per_page ?? 20,
					current_page: clearingUni.courses?.current_page ?? 1,
					has_more_pages: clearingUni.courses?.has_more_pages ?? false,
					total: clearingUni.courses?.total ?? 0,
					filters: clearingUni.courses?.filters ?? null,
					data: clearingCourses,
				},
			},
			shouldLoadCourses: false,
			isLoading: false,
			isLoadingPagination: false,
			error: null,
		};
	}),

	on(actions.updateClearingUniCourses, (state, { clearingUni }) => {
		return {
			...state,
			courses: null,
			uni: null,
			clearingCourses: null,
			clearingUni: {
				...state.clearingUni,
				...clearingUni,
			},
			shouldLoadCourses: false,
			isLoading: false,
			isLoadingPagination: false,
			error: null,
		};
	}),

	// payload
	on(actions.updateCourseSearchPayload, (state, { payload }) => {
		const courseSearchPayload = Object.fromEntries(
			Object.entries(payload).filter(([_, value]) => value !== undefined),
		);

		return {
			...state,
			universityCourseSearchPayload: {
				...UNIVERSITY_COURSE_SEARCH_DEFAULT_PAYLOAD,
				degreeLevel: 'ALL_CLEARING' as DegreeLevelPayload,
			},
			courseSearchPayload: {
				...state.courseSearchPayload,
				...courseSearchPayload,
			},
		};
	}),

	on(actions.updateClearingCourseSearchPayload, (state, { payload }) => {
		const clearingCourseSearchPayload = Object.fromEntries(
			Object.entries(payload).filter(([_, value]) => value !== undefined),
		);

		return {
			...state,
			clearingUniCourseSearchPayload: {
				...UNIVERSITY_COURSE_SEARCH_DEFAULT_PAYLOAD,
				degreeLevel: 'ALL_CLEARING' as DegreeLevelPayload,
			},
			clearingCourseSearchPayload: {
				...state.clearingCourseSearchPayload,
				...clearingCourseSearchPayload,
			},
		};
	}),

	on(actions.updateUniCourseSearchPayload, (state, { payload }) => {
		const uniCourseSearchPayload = Object.fromEntries(
			Object.entries(payload).filter(([_, value]) => value !== undefined),
		);

		return {
			...state,
			courseSearchPayload: { ...COURSE_SEARCH_DEFAULT_PAYLOAD },
			uniCourseSearchPayload: {
				...state.uniCourseSearchPayload,
				...uniCourseSearchPayload,
			},
		};
	}),

	on(actions.updateClearingUniCourseSearchPayload, (state, { payload }) => {
		const clearingUniCourseSearchPayload = Object.fromEntries(
			Object.entries(payload).filter(([_, value]) => value !== undefined),
		);

		return {
			...state,
			clearingCourseSearchPayload: {
				...COURSE_SEARCH_DEFAULT_PAYLOAD,
				degreeLevel: 'ALL_CLEARING' as DegreeLevelPayload,
			},
			clearingUniCourseSearchPayload: {
				...state.clearingUniCourseSearchPayload,
				...clearingUniCourseSearchPayload,
			},
		};
	}),

	// pills
	on(actions.updateFilterPills, (state, { pills }) => {
		return {
			...state,
			filterPills: pills,
		};
	}),

	on(actions.removeFilterPill, (state, { pill }) => {
		return {
			...state,
			filterToBeRemoved: pill,
		};
	}),

	on(actions.updateCourseSearchError, (state, { error }) => {
		return {
			...state,
			isLoading: false,
			isLoadingPagination: false,
			shouldLoadCourses: false,
			error,
		};
	}),
);

export function reducer(state: CourseSearchState | undefined, action: Action) {
	return courseSearchReducer(state, action);
}
